import { z } from 'zod';
import { brandedUuid } from '../FlavorSchema.js';
export const DeliverableIdSchema = brandedUuid('Invalid deliverable ID format');
export const ProductIdSchema = brandedUuid('Invalid product ID format');
export const ProductOfferingIdSchema = brandedUuid('Invalid product offering ID format');
export const PricingIdSchema = brandedUuid('Invalid pricing ID format');
export const BillingProfileIdSchema = brandedUuid('Invalid billing profile ID format');
export const RatedPriceIdSchema = brandedUuid('Invalid rated price ID format');
export const ContractItemIdSchema = brandedUuid('Invalid contract item ID format');
export const ContractBilledItemIdSchema = brandedUuid('Invalid contract billed item ID format');
export const ContractInvoiceIdSchema = brandedUuid('Invalid contract invoice ID format');
export const ContractIdSchema = brandedUuid('Invalid contract ID format');
export const InvoiceIdSchema = brandedUuid('Invalid invoice ID format');
export const InvoiceItemIdSchema = brandedUuid('Invalid invoice item ID format');
export const TaxRateIdSchema = brandedUuid('Invalid tax rate ID format');
export const PriceBookIdSchema = brandedUuid('Invalid price book ID format');
export const FeeProposalIdSchema = brandedUuid('Invalid fee proposal ID format');
export const SyncTaskIdSchema = brandedUuid('Invalid sync task ID format');
export const StripeProductIdSchema = z
    .string()
    .min(1)
    .regex(/^prod_[A-Za-z0-9]+$/, 'Invalid Stripe product ID format')
    .transform((val) => val);
export const StripeCustomerIdSchema = z
    .string()
    .min(1)
    .regex(/^cus_[A-Za-z0-9]+$/, 'Invalid Stripe customer ID format')
    .transform((val) => val);
export const StripeAccountIdSchema = z
    .string()
    .min(1)
    .regex(/^acct_[A-Za-z0-9]+$/, 'Invalid Stripe account ID format')
    .transform((val) => val);
export const StripeInvoiceItemIdSchema = z
    .string()
    .min(1)
    // Matches both ii_* (invoice items) and il_* (invoice line items)
    .regex(/^i[il]_[A-Za-z0-9]+$/, 'Invalid Stripe invoice item ID format')
    .transform((val) => val);
export const StripeInvoiceIdSchema = z
    .string()
    .min(1)
    .regex(/^in_[A-Za-z0-9]+$/, 'Invalid Stripe invoice ID format')
    .transform((val) => val);
export const StripeTaxRateIdSchema = z
    .string()
    .min(1)
    .regex(/^txr_[A-Za-z0-9]+$/, 'Invalid Stripe tax rate ID format')
    .transform((val) => val);
export const StripePaymentMethodIdSchema = z
    .string()
    .min(1)
    .regex(/^pm_[A-Za-z0-9]+$/, 'Invalid Stripe payment method ID format')
    .transform((val) => val);
export const StripePaymentIntentIdSchema = z
    .string()
    .min(1)
    .regex(/^pi_[A-Za-z0-9]+$/, 'Invalid Stripe payment intent ID format')
    .transform((val) => val);
export const StripeChargeIdSchema = z
    .string()
    .min(1)
    // Matches both ch_* (card charges) and py_* (bank debit charges)
    .regex(/^(ch|py)_[A-Za-z0-9]+$/, 'Invalid Stripe charge ID format')
    .transform((val) => val);
// ============================================================================
// Enum Schemas
// ============================================================================
/**
 * Billing currencies supported by the platform
 */
export const BillingCurrencySchema = z.enum(['USD', 'EUR', 'GBP']);
/**
 * Types of payment methods accepted
 */
export const BillingInstrumentSchema = z.enum(['bank_debit', 'bank_transfer', 'credit_card']);
/**
 * Types of tax computation
 *
 * - `exclusive`: Tax is added ON TOP of the base amount (subtotal + tax = total)
 * - `inclusive`: Tax is INSIDE the base amount (subtotal = total, tax is a portion within)
 */
export const TaxBehaviorSchema = z.enum(['exclusive', 'inclusive']);
/**
 * Type guard for exclusive tax behavior.
 * Use for cleaner conditionals: `if (isExclusiveTax(behavior)) { ... }`
 */
export function isExclusiveTax(behavior) {
    return behavior === 'exclusive';
}
/**
 * Type guard for inclusive tax behavior.
 * Use for cleaner conditionals: `if (isInclusiveTax(behavior)) { ... }`
 */
export function isInclusiveTax(behavior) {
    return behavior === 'inclusive';
}
/**
 * Invoice status values
 */
export const InvoiceStatusSchema = z.enum(['initializing', 'draft', 'open', 'paid', 'uncollectible', 'void', 'error']);
/**
 * Payment intent status values from Stripe
 */
export const PaymentIntentStatusSchema = z.enum([
    'requires_payment_method',
    'requires_confirmation',
    'requires_action',
    'processing',
    'requires_capture',
    'canceled',
    'succeeded',
]);
/**
 * Charge status values from Stripe
 */
export const ChargeStatusSchema = z.enum(['succeeded', 'pending', 'failed']);
/**
 * Invoice payment types - represents payment attempts associated with an invoice.
 * Uses discriminated union to handle different payment types from Stripe.
 */
export const InvoicePaymentSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('stripe_payment_intent'),
        isDefault: z.boolean(),
        paymentIntentStatus: PaymentIntentStatusSchema,
        externalLinks: z.object({
            stripePaymentIntentId: StripePaymentIntentIdSchema,
            stripeAccountId: StripeAccountIdSchema,
        }),
    }),
    z.object({
        type: z.literal('stripe_payment_record'),
        isDefault: z.boolean(),
        externalLinks: z.object({
            stripePaymentRecordId: z.string().min(1),
            stripeAccountId: StripeAccountIdSchema,
        }),
    }),
    z.object({
        type: z.literal('stripe_charge'),
        isDefault: z.boolean(),
        chargeStatus: ChargeStatusSchema,
        externalLinks: z.object({
            stripeChargeId: StripeChargeIdSchema,
            stripeAccountId: StripeAccountIdSchema,
        }),
    }),
]);
// ============================================================================
// Label Helpers
// ============================================================================
export { getBillingCurrencyChoices, getBillingCurrencyLabel, getBillingInstrumentChoices, getBillingInstrumentLabel, } from './BillingLabels.js';
//# sourceMappingURL=BillingSchemas.js.map